import { Fragment, useState, useEffect } from "react";
import { Disclosure, Menu, Transition } from "@headlessui/react";
import { Bars3Icon, XMarkIcon } from "@heroicons/react/24/outline";
import { useRouter } from "next/router";
import Image from "next/image";
import logo from "@/public/cw_icon.svg";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

const navigation = [
  { name: "Home", href: "/" },
  { name: "Blog", href: "/blog" },
  { name: "Clans", href: "/clan" },
  { name: "Codes", href: "/codes" },
  { name: "Verify", href: "/verify" },
  { name: "Support", href: "/support" },
];

const DEFAULT_AVATAR = "https://www.roblox.com/headshot-thumbnail/image?userId=1&width=150&height=150&format=png";

function getRobloxAvatar(user) {
  if (!user) return DEFAULT_AVATAR;
  if (user.picture && String(user.picture).startsWith("http")) return user.picture;
  if (user.avatar && String(user.avatar).startsWith("http")) return user.avatar;
  if (user.id) {
    return `https://www.roblox.com/headshot-thumbnail/image?userId=${user.id}&width=150&height=150&format=png`;
  }
  return DEFAULT_AVATAR;
}

function getDisplayName(user) {
  return user?.nickname || user?.name || user?.preferred_username || user?.username || user?.global_name || "User";
}

function getInitials(user) {
  return getDisplayName(user).charAt(0).toUpperCase();
}

function isActivePath(pathname, href) {
  if (href === "/") return pathname === "/";
  return pathname === href || pathname.startsWith(`${href}/`);
}

function notifyAuthChanged() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event("cw-auth-changed"));
  }
}

export default function NavBar() {
  const router = useRouter();
  const [session, setSession] = useState(null);
  const [staff, setStaff] = useState(false);
  const [renderBar, setRenderBar] = useState(false);
  const [authReady, setAuthReady] = useState(false);

  const noRenderOn = [/^\/staff/, /^\/staff.*/, /^\/ip-ban/];

  useEffect(() => {
    setRenderBar(!noRenderOn.some((pattern) => pattern.test(router.pathname)));
  }, [router.pathname]);

  useEffect(() => {
    const fetchSession = () => {
      fetch("/api/auth/auth")
        .then((res) => res.json())
        .then((data) => {
          if (data.success) {
            setSession(data.session);
            setStaff(data?.mod || data?.admin || false);
          } else {
            setSession(null);
            setStaff(false);
          }
        })
        .catch(() => {
          setSession(null);
          setStaff(false);
        })
        .finally(() => setAuthReady(true));
    };

    fetchSession();

    const onAuthChanged = () => fetchSession();
    const onVisible = () => {
      if (document.visibilityState === "visible") fetchSession();
    };
    const onRoute = () => fetchSession();

    window.addEventListener("cw-auth-changed", onAuthChanged);
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onAuthChanged);
    router.events.on("routeChangeComplete", onRoute);

    const interval = setInterval(fetchSession, 1000 * 30);

    return () => {
      window.removeEventListener("cw-auth-changed", onAuthChanged);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onAuthChanged);
      router.events.off("routeChangeComplete", onRoute);
      clearInterval(interval);
    };
  }, [router.events]);

  const signOut = async () => {
    setSession(null);
    setStaff(false);
    try {
      await fetch("/api/auth/logout", {
        method: "POST",
        headers: { Accept: "application/json" },
      });
    } catch {
      // still leave the local UI logged out
    }
    notifyAuthChanged();
    router.replace("/");
  };

  if (!renderBar) return null;

  const user = session?.user;
  const avatarUrl = getRobloxAvatar(user);
  const displayName = getDisplayName(user);
  const username = user?.preferred_username || user?.username || user?.name;

  return (
    <Disclosure as="nav" className="relative z-30">
      {({ open }) => (
        <div
          className={`border-b border-white/15 transition-colors duration-200 ${
            open ? "bg-black" : "bg-black/60 backdrop-blur-md"
          }`}
        >
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid h-[4.25rem] grid-cols-[auto_1fr_auto] items-center gap-4">
              {/* Left: logo + mobile toggle */}
              <div className="flex items-center gap-2">
                <Disclosure.Button className="inline-flex items-center justify-center rounded-md p-2 text-white/80 hover:bg-white/10 hover:text-white transition sm:hidden focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30">
                  <span className="sr-only">Open main menu</span>
                  {open ? (
                    <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                  ) : (
                    <Bars3Icon className="h-6 w-6" aria-hidden="true" />
                  )}
                </Disclosure.Button>

                <button
                  type="button"
                  onClick={() => router.push("/")}
                  className="flex items-center focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30 rounded"
                  aria-label="Combat Warriors home"
                >
                  <Image
                    src={logo}
                    alt="Combat Warriors"
                    width={44}
                    height={44}
                    className="h-10 w-10 sm:h-11 sm:w-11"
                    priority
                  />
                </button>
              </div>

              {/* Center: desktop links */}
              <div className="hidden sm:flex items-center justify-center">
                <div className="flex items-center gap-7">
                  {navigation.map((item) => {
                    const active = isActivePath(router.pathname, item.href);
                    return (
                      <button
                        key={item.name}
                        type="button"
                        onClick={() => router.push(item.href)}
                        className={`relative pb-1 text-[15px] tracking-wide transition ${
                          active
                            ? "font-semibold text-white"
                            : "font-medium text-white/65 hover:text-white"
                        }`}
                      >
                        {item.name}
                        <span
                          className={`pointer-events-none absolute left-1/2 -translate-x-1/2 bottom-0 h-[2px] rounded-full bg-white transition-all duration-200 ${
                            active ? "w-full" : "w-0"
                          }`}
                        />
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Right: account */}
              <div className="flex justify-end">
                {!authReady ? (
                  <div className="h-9 w-24 rounded-full bg-white/10 animate-pulse" />
                ) : session ? (
                  <Menu as="div" className="relative">
                    <Menu.Button className="group flex items-center gap-2.5 rounded-full border border-white/15 bg-white/[0.04] py-1 pl-1 pr-3 transition hover:border-white/30 hover:bg-white/[0.08] focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30">
                      <span className="sr-only">Open user menu</span>
                      <Avatar className="h-8 w-8">
                        <AvatarImage src={avatarUrl} alt={displayName} />
                        <AvatarFallback className="bg-zinc-700 text-white text-xs font-semibold">
                          {getInitials(user)}
                        </AvatarFallback>
                      </Avatar>
                      <span className="hidden md:block max-w-[9rem] truncate text-sm font-medium text-white">
                        {displayName}
                      </span>
                    </Menu.Button>

                    <Transition
                      as={Fragment}
                      enter="transition ease-out duration-100"
                      enterFrom="transform opacity-0 scale-95"
                      enterTo="transform opacity-100 scale-100"
                      leave="transition ease-in duration-75"
                      leaveFrom="transform opacity-100 scale-100"
                      leaveTo="transform opacity-0 scale-95"
                    >
                      <Menu.Items className="absolute right-0 z-10 mt-2 w-56 origin-top-right overflow-hidden rounded-xl border border-white/10 bg-[#111] py-1 shadow-2xl focus:outline-none">
                        <div className="border-b border-white/10 px-4 py-3">
                          <p className="truncate text-sm font-medium text-white">
                            {displayName}
                          </p>
                          {username && (
                            <p className="truncate text-xs text-white/45">
                              @{username}
                            </p>
                          )}
                        </div>

                        <Menu.Item>
                          {({ active }) => (
                            <button
                              type="button"
                              onClick={() => router.push("/profile")}
                              className={`block w-full px-4 py-2.5 text-left text-sm text-white/85 transition ${
                                active ? "bg-white/10 text-white" : ""
                              }`}
                            >
                              Your Profile
                            </button>
                          )}
                        </Menu.Item>
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              type="button"
                              onClick={() => router.push("/clan/add")}
                              className={`block w-full px-4 py-2.5 text-left text-sm text-white/85 transition ${
                                active ? "bg-white/10 text-white" : ""
                              }`}
                            >
                              Add Clan
                            </button>
                          )}
                        </Menu.Item>
                        {staff ? (
                          <Menu.Item>
                            {({ active }) => (
                              <button
                                type="button"
                                onClick={() => router.push("/staff")}
                                className={`block w-full px-4 py-2.5 text-left text-sm text-white/85 transition ${
                                  active ? "bg-white/10 text-white" : ""
                                }`}
                              >
                                Staff Panel
                              </button>
                            )}
                          </Menu.Item>
                        ) : null}
                        <div className="my-1 border-t border-white/10" />
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              type="button"
                              onClick={signOut}
                              className={`block w-full px-4 py-2.5 text-left text-sm text-red-300 transition ${
                                active ? "bg-white/10" : ""
                              }`}
                            >
                              Sign out
                            </button>
                          )}
                        </Menu.Item>
                      </Menu.Items>
                    </Transition>
                  </Menu>
                ) : (
                  <button
                    type="button"
                    onClick={() => router.push("/login")}
                    className="rounded-full bg-white px-5 py-2 text-sm font-semibold text-black transition hover:bg-white/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
                  >
                    Sign In
                  </button>
                )}
              </div>
            </div>
          </div>

          <Disclosure.Panel className="sm:hidden border-t border-white/10 bg-black">
            <div className="space-y-0.5 px-3 py-3">
              {navigation.map((item) => {
                const active = isActivePath(router.pathname, item.href);
                return (
                  <Disclosure.Button
                    key={item.name}
                    as="button"
                    type="button"
                    onClick={() => router.push(item.href)}
                    className={`block w-full rounded-md px-3 py-2.5 text-left text-base transition ${
                      active
                        ? "bg-white/10 font-semibold text-white"
                        : "font-medium text-white/70 hover:bg-white/5 hover:text-white"
                    }`}
                  >
                    {item.name}
                  </Disclosure.Button>
                );
              })}
            </div>
          </Disclosure.Panel>
        </div>
      )}
    </Disclosure>
  );
}
